import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../components/Modal';
import UserForm from '../components/UserForm';
import Pagination from '../components/Pagination';
import api from '../api/axiosConfig';
import { showSuccessToast, showErrorAlert, showConfirmDialog } from '../utils/alert';
import './ServicePage.css';

const ManageUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const API_URL = '/api/users';

  const fetchUsers = useCallback(async (page = 1, limit = itemsPerPage, keyword = debouncedSearchTerm) => {
    try {
      const response = await api.get(`${API_URL}?keyword=${keyword}&page=${page}&limit=${limit}`);
      setUsers(response.data.data);
      setTotalItems(response.data.totalItems);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
    } catch (error) {
      console.error("Gagal mengambil data pengguna:", error);
      showErrorAlert("Gagal memuat data pengguna.");
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    const timerId = setTimeout(() => { setDebouncedSearchTerm(searchTerm); setCurrentPage(1); }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [debouncedSearchTerm, currentPage, itemsPerPage, fetchUsers]);

  const handlePageChange = (pageNumber) => { fetchUsers(pageNumber); };
  const handleItemsPerPageChange = (limit) => { setItemsPerPage(limit); fetchUsers(1, limit); };
  
  const handleSave = async (userData) => {
    const isEditing = !!currentUser;
    const url = isEditing ? `${API_URL}/${currentUser._id}` : API_URL;
    const method = isEditing ? 'put' : 'post';
    if (isEditing && !userData.password) {
      delete userData.password;
    }

    try {
      await api[method](url, userData);
      setModalOpen(false);
      fetchUsers(currentPage);
      showSuccessToast(isEditing ? 'User berhasil diperbarui!' : 'User berhasil ditambahkan!');
    } catch (error) {
      console.error("Gagal menyimpan data user:", error);
      showErrorAlert(error.response?.data?.message || "Gagal menyimpan data.");
    }
  };

  const handleDelete = async (id) => {
    const result = await showConfirmDialog();
    if (result.isConfirmed) {
      try {
        await api.delete(`${API_URL}/${id}`);
        fetchUsers(currentPage);
        showSuccessToast('User berhasil dihapus!');
      } catch (error) {
        console.error("Gagal menghapus user:", error);
        showErrorAlert("Gagal menghapus user.");
      }
    }
  };
  
  const openModal = (user = null) => {
    setCurrentUser(user);
    setModalOpen(true);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <p>Admin / Manage Users</p>
        <h1>Manajemen Pengguna</h1>
      </div>
      <div className="content-card">
        <div className="toolbar">
          <button className="btn-primary" onClick={() => openModal()}>+ Tambah User</button>
          <input 
            type="text"
            placeholder="Cari Nama, Email, Role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginLeft: 'auto' }}
          />
        </div>
        <div className="table-responsive-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Nama</th>
                <th>Email</th>
                <th>Role</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user._id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td><span className={`role-badge role-${user.role}`}>{user.role}</span></td>
                  <td className="action-buttons">
                    <button onClick={() => openModal(user)} title="Edit">✏️</button>
                    <button onClick={() => handleDelete(user._id)} title="Hapus">🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />

      </div>
      <Modal show={isModalOpen} onClose={() => setModalOpen(false)} title={currentUser ? "Edit User" : "Tambah User"}>
        <UserForm existingData={currentUser} onSave={handleSave} onCancel={() => setModalOpen(false)} />
      </Modal>
    </div>
  );
};

export default ManageUsersPage;